import { Box, Button, Popover } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useV2Theme } from "../../hooks/useV2Theme";
import { FONT_MONO } from "../../theme/v2Tokens";
import FilterChip from "./FilterChip";
import FilterPopoverContent from "./FilterPopovers";
import { SearchFilters, FILTER_CHIP_DEFS, FilterChipDef } from "./filterChipDefs";

interface FilterBarProps {
  readonly filters: SearchFilters;
  readonly onFiltersChange: (filters: SearchFilters) => void;
  readonly selectedCount: number;
  readonly onBulkExport: (event: React.MouseEvent<HTMLButtonElement>) => void;
  readonly onBulkEdit: () => void;
  readonly customerId: string;
  readonly siteMap?: Map<string, string>;
}

export default function FilterBar({ // NOSONAR
  filters,
  onFiltersChange,
  selectedCount,
  onBulkExport,
  onBulkEdit,
  customerId,
  siteMap,
}: FilterBarProps) {
  const { t } = useTranslation(["search", "common"]);
  const { mode } = useV2Theme();
  const [query, setQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // -- Responsive overflow ---------------------------------------------------
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxVisible, setMaxVisible] = useState(5);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      const chipWidth = 85;
      const moreButtonWidth = 70;
      const available = width - moreButtonWidth;
      setMaxVisible(Math.max(3, Math.floor(available / chipWidth)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Split chips into visible and overflow
  const visibleChips = FILTER_CHIP_DEFS.slice(0, maxVisible);
  const overflowChips = FILTER_CHIP_DEFS.slice(maxVisible);

  // Also show chips with active values even if they'd be in overflow
  const activeOverflowChips = overflowChips.filter((c) => c.hasValue(filters));
  const inactiveOverflowChips = overflowChips.filter((c) => !c.hasValue(filters));

  // Merge: visible + active-overflow = shown as chips; rest in +More menu
  const shownChips = [...visibleChips, ...activeOverflowChips];
  const moreChips = inactiveOverflowChips;

  // -- +More menu state ------------------------------------------------------
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [morePopoverChip, setMorePopoverChip] = useState<FilterChipDef | null>(null);
  const [morePopoverAnchor, setMorePopoverAnchor] = useState<HTMLElement | null>(null);

  const handleMoreClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setMoreAnchor(e.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreAnchor(null);
  };

  const handleMoreChipClick = (chipDef: FilterChipDef, e: React.MouseEvent<HTMLButtonElement>) => {
    setMoreAnchor(null);
    setMorePopoverChip(chipDef);
    setMorePopoverAnchor(e.currentTarget);
  };

  const handleMorePopoverClose = () => {
    setMorePopoverChip(null);
    setMorePopoverAnchor(null);
  };

  // Count active overflow filters for badge
  const activeOverflowCount = overflowChips.filter((c) => c.hasValue(filters)).length;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        px: 2,
        py: 1.25,
        backgroundColor: "var(--bg-surface)",
        borderBottom: "1px solid var(--border-subtle)",
        flexWrap: "nowrap",
        minHeight: 56,
      }}
    >
      {/* Search input — collapsed when unfocused, expands on focus */}
      <Box
        onClick={() => {
          setSearchFocused(true);
          setTimeout(() => searchInputRef.current?.focus(), 0);
        }}
        sx={{
          position: "relative",
          width: searchFocused ? 320 : 38,
          minWidth: searchFocused ? 320 : 38,
          height: searchFocused ? "auto" : 34,
          flexShrink: 0,
          transition: "width 200ms ease, min-width 200ms ease",
          cursor: searchFocused ? "text" : "pointer",
          ...(!searchFocused && {
            border: "1px solid var(--border-mid)",
            borderRadius: "8px",
            backgroundColor: "var(--bg-raised)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "&:hover": {
              borderColor: "var(--border-strong)",
              backgroundColor: "var(--bg-hover)",
            },
          }),
        }}
      >
        {/* Magnifying glass icon */}
        <Box
          component="svg"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          sx={{
            position: searchFocused ? "absolute" : "relative",
            left: searchFocused ? 10 : "auto",
            top: searchFocused ? "50%" : "auto",
            transform: searchFocused ? "translateY(-50%)" : "none",
            width: searchFocused ? 15 : 17,
            height: searchFocused ? 15 : 17,
            color: searchFocused ? "var(--text-tertiary)" : "var(--text-secondary)",
            pointerEvents: "none",
            transition: "width 200ms ease, height 200ms ease",
          }}
        >
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </Box>
        <Box
          ref={searchInputRef}
          component="input"
          type="text"
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => { if (!query) setSearchFocused(false); }}
          placeholder={searchFocused ? t("search:searchPlaceholder") : ""}
          sx={{
            width: "100%",
            pl: "34px",
            pr: "12px",
            py: "7px",
            fontSize: "0.8125rem",
            fontFamily: "inherit",
            color: "var(--text-primary)",
            backgroundColor: searchFocused ? "var(--bg-raised)" : "transparent",
            border: searchFocused ? "1px solid var(--border-subtle)" : "1px solid transparent",
            borderRadius: "8px",
            outline: "none",
            boxSizing: "border-box",
            transition: "all 200ms ease",
            display: searchFocused ? "block" : "none",
            "&::placeholder": {
              color: "var(--text-muted)",
            },
            "&:focus": {
              borderColor: "var(--border-mid)",
              backgroundColor: "var(--bg-surface)",
              opacity: 1,
            },
          }}
        />
      </Box>

      {/* Filter chips container (measured for responsive overflow) */}
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        {shownChips.map((chipDef) => (
          <FilterChip
            key={chipDef.id}
            chipDef={chipDef}
            filters={filters}
            onFiltersChange={onFiltersChange}
            mode={mode}
            customerId={customerId}
            siteMap={siteMap}
          />
        ))}

        {/* +More button */}
        {moreChips.length > 0 && (
          <Box
            component="button"
            onClick={handleMoreClick}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              px: "10px",
              py: "6px",
              fontSize: "0.75rem",
              fontWeight: 500,
              color: "var(--text-secondary)",
              backgroundColor: "transparent",
              border: "1px solid var(--border-mid)",
              borderRadius: "20px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
              fontFamily: "inherit",
              transition: "border-color 150ms ease, background-color 150ms ease",
              "&:hover": {
                borderColor: "var(--border-strong)",
                backgroundColor: "var(--bg-hover)",
                color: "var(--text-primary)",
              },
            }}
          >
            {t("search:more")}
            {activeOverflowCount > 0 && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: "var(--accent)",
                  color: "#fff",
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {activeOverflowCount}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* +More popover — list of overflow chip options */}
      <Popover
        open={Boolean(moreAnchor)}
        anchorEl={moreAnchor}
        onClose={handleMoreClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: mode === "dark" ? "#1A2332" : "#FFFFFF",
              border: `1px solid ${mode === "dark" ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.12)"}`,
              borderRadius: "8px",
              boxShadow: mode === "dark"
                ? "0 8px 24px rgba(0,0,0,0.5)"
                : "0 8px 24px rgba(0,0,0,0.12)",
              color: mode === "dark" ? "#F1F5F9" : "#0F172A",
              mt: "4px",
              maxHeight: 400,
              overflowY: "auto",
              p: 1,
              minWidth: 200,
            },
          },
        }}
      >
        {moreChips.map((chipDef) => (
          <Box
            key={chipDef.id}
            component="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleMoreChipClick(chipDef, e)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              textAlign: "left",
              px: "12px",
              py: "8px",
              fontSize: "0.8125rem",
              fontFamily: "inherit",
              color: mode === "dark" ? "#94A3B8" : "#475569",
              bgcolor: "transparent",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              "&:hover": {
                bgcolor: mode === "dark" ? "#1E293B" : "#E2E8F0",
                color: mode === "dark" ? "#F1F5F9" : "#0F172A",
              },
            }}
          >
            {chipDef.label}
          </Box>
        ))}
      </Popover>

      {/* Popover launched from +More menu item */}
      {morePopoverChip && (
        <Popover
          open={Boolean(morePopoverAnchor)}
          anchorEl={morePopoverAnchor}
          onClose={handleMorePopoverClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: mode === "dark" ? "#1A2332" : "#FFFFFF",
                border: `1px solid ${mode === "dark" ? "rgba(148,163,184,0.15)" : "rgba(15,23,42,0.12)"}`,
                borderRadius: "8px",
                boxShadow: mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.5)"
                  : "0 8px 24px rgba(0,0,0,0.12)",
                color: mode === "dark" ? "#F1F5F9" : "#0F172A",
                mt: "4px",
              },
            },
          }}
        >
          <FilterPopoverContent
            chipDef={morePopoverChip}
            filters={filters}
            onFiltersChange={onFiltersChange}
            onClose={handleMorePopoverClose}
            mode={mode}
            customerId={customerId}
          />
        </Popover>
      )}

      {/* Ask Archie pill — hidden for now */}
      {/* {hasArchie && ( ... )} */}

      {/* Clear all filters button — only visible when any filter is active */}
      {FILTER_CHIP_DEFS.some((c) => c.hasValue(filters)) && (
        <Box
          component="button"
          onClick={() => onFiltersChange({})}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            px: "10px",
            py: "4px",
            borderRadius: "20px",
            border: "1px solid rgba(251,113,133,0.3)",
            bgcolor: "rgba(251,113,133,0.08)",
            color: "#FB7185",
            fontSize: "0.72rem",
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
            flexShrink: 0,
            "&:hover": {
              bgcolor: "rgba(251,113,133,0.15)",
              borderColor: "rgba(251,113,133,0.5)",
            },
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 12, height: 12 }}>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          {t("common:clear")}
        </Box>
      )}

      {/* Spacer */}
      <Box sx={{ flex: "none", width: 8 }} />

      {/* Selected count + bulk actions */}
      {selectedCount > 0 && (
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            fontFamily: FONT_MONO,
            color: "var(--accent)",
            whiteSpace: "nowrap",
            flexShrink: 0,
            userSelect: "none",
            fontWeight: 600,
          }}
        >
          {t("search:selected", { count: selectedCount })}
        </Box>
      )}

      <Button
        variant="outlined"
        size="small"
        disabled={selectedCount < 2}
        onClick={onBulkExport}
        sx={{
          fontSize: "0.75rem",
          fontWeight: 600,
          color: selectedCount >= 2 ? "var(--text-secondary)" : "var(--text-muted)",
          borderColor: "var(--border-mid)",
          borderRadius: "8px",
          textTransform: "none",
          flexShrink: 0,
          opacity: selectedCount >= 2 ? 1 : 0.5,
          "&:hover": {
            borderColor: "var(--border-strong)",
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
          },
        }}
      >
        {t("common:bulkExport")}
      </Button>

      <Button
        variant="outlined"
        size="small"
        disabled={selectedCount === 0}
        onClick={onBulkEdit}
        sx={{
          fontSize: "0.75rem",
          fontWeight: 600,
          color: selectedCount > 0 ? "var(--text-secondary)" : "var(--text-muted)",
          borderColor: "var(--border-mid)",
          borderRadius: "8px",
          textTransform: "none",
          flexShrink: 0,
          opacity: selectedCount > 0 ? 1 : 0.5,
          "&:hover": {
            borderColor: "var(--border-strong)",
            backgroundColor: "var(--bg-hover)",
            color: "var(--text-primary)",
          },
        }}
      >
        {t("common:bulkEdit")}
      </Button>
    </Box>
  );
}
